import React from 'react';
import { i18n } from '../i18n/i18n';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  title: string;
  /** Prompt body, e.g. Are you sure you want to delete "X"? */
  message: React.ReactNode;
  /** Optional secondary note (defaults to the shared "this can't be undone" line). */
  note?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
  /** When set, shows an error state with a single OK button instead of confirm/cancel. */
  error?: string;
}

/**
 * Shared confirmation dialog for destructive actions (rooms, tools, rewards, …).
 * Extracted from the original room-delete modal so every delete uses the same look/behaviour.
 */
const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen, title, message, note, confirmLabel, onConfirm, onCancel, isDeleting, error,
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">{title}</h3>
        {!error ? (
          <>
            <p className="text-gray-600 dark:text-gray-300 mb-2">{message}</p>
            <p className="text-gray-500 dark:text-gray-400 mb-4">{note ?? i18n.t('admin_panel.irreversible_note')}</p>
            <div className="flex gap-3">
              <button className="btn btn-cancel flex-1" onClick={onCancel} disabled={!!isDeleting}>
                {i18n.t('admin_panel.cancel_btn')}
              </button>
              <button className="btn btn-danger flex-1" onClick={onConfirm} disabled={!!isDeleting}>
                {confirmLabel ?? i18n.t('admin_panel.delete_btn')}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-red-700 dark:text-red-300 mb-6">{error}</p>
            <div className="flex justify-end">
              <button className="btn btn-primary" onClick={onCancel}>{i18n.t('common.ok')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmDeleteModal;
